import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { TabsContent } from "../ui/tabs";
import { Switch } from "../ui/switch";
import {
  Share2, CheckCircle, XCircle, Loader2, Send, Linkedin,
  Twitter, Facebook, Instagram, ChevronDown, ChevronUp,
  RefreshCw, Settings,
} from "lucide-react";
import { BACKEND_URL } from "../../lib/backend";

const API = `${BACKEND_URL}/api`;

const PLATFORMS = [
  {
    id: "linkedin", label: "LinkedIn", icon: Linkedin, color: "#0077B5",
    fields: [
      { key: "access_token", label: "Access Token", hint: "Bearer token from LinkedIn Developer App" },
      { key: "org_id",       label: "Organization ID", hint: "Numeric ID of your LinkedIn Company Page" },
    ],
  },
  {
    id: "twitter", label: "Twitter / X", icon: Twitter, color: "#000",
    fields: [
      { key: "api_key",              label: "API Key (Consumer Key)" },
      { key: "api_secret",           label: "API Secret (Consumer Secret)" },
      { key: "access_token",         label: "Access Token" },
      { key: "access_token_secret",  label: "Access Token Secret" },
    ],
  },
  {
    id: "facebook", label: "Facebook", icon: Facebook, color: "#1877F2",
    fields: [
      { key: "page_token", label: "Page Access Token", hint: "Long-lived page token from Meta for Developers" },
      { key: "page_id",    label: "Page ID", hint: "Numeric ID of your Facebook Page" },
    ],
  },
  {
    id: "instagram", label: "Instagram", icon: Instagram, color: "#E1306C",
    fields: [
      { key: "token",   label: "Access Token", hint: "Instagram Graph API token (requires a connected Facebook Page)" },
      { key: "user_id", label: "Business Account ID", hint: "Instagram Business Account ID" },
    ],
  },
];

const EMPTY_CREDS = {
  linkedin:  { access_token: "", org_id: "" },
  twitter:   { api_key: "", api_secret: "", access_token: "", access_token_secret: "" },
  facebook:  { page_token: "", page_id: "" },
  instagram: { token: "", user_id: "" },
};

export default function SocialTab({ dm, authHeader }) {
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [posting, setPosting]     = useState(false);
  const [isSet, setIsSet]         = useState({});
  const [settings, setSettings]   = useState({ auto_post_on_publish: false, enabled_platforms: [] });
  const [editCreds, setEditCreds] = useState(EMPTY_CREDS);
  const [testResults, setTestResults] = useState({});
  const [testing, setTesting]     = useState({});
  const [manualPlatforms, setManualPlatforms] = useState([]);
  const [postResults, setPostResults] = useState(null);
  const [expanded, setExpanded]   = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/social-config`, { headers: authHeader() });
      setIsSet(res.data.is_set || {});
      setSettings(res.data.settings || { auto_post_on_publish: false, enabled_platforms: [] });
    } catch (e) {
      if (e.response?.status !== 401) toast.error("Failed to load social config");
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const saveCreds = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/social-config`, { credentials: editCreds, settings }, { headers: authHeader() });
      toast.success("Social credentials saved!");
      setEditCreds(EMPTY_CREDS);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const testPlatform = async (platform) => {
    setTesting(p => ({ ...p, [platform]: true }));
    setTestResults(p => ({ ...p, [platform]: null }));
    try {
      const res = await axios.post(`${API}/admin/social-config/test`, { platform }, { headers: authHeader() });
      setTestResults(p => ({ ...p, [platform]: res.data }));
    } catch (e) {
      setTestResults(p => ({ ...p, [platform]: { success: false, message: e.response?.data?.detail || "Test failed" } }));
    } finally {
      setTesting(p => ({ ...p, [platform]: false }));
    }
  };

  const postBriefNow = async () => {
    if (!manualPlatforms.length) { toast.error("Select at least one platform"); return; }
    setPosting(true);
    setPostResults(null);
    try {
      const res = await axios.post(`${API}/admin/brief/post-social`, { platforms: manualPlatforms }, { headers: authHeader() });
      setPostResults(res.data.results);
      const ok = res.data.posted;
      if (ok === res.data.total) toast.success(`Brief posted to ${ok} platform${ok !== 1 ? "s" : ""}!`);
      else toast.warning(`${ok}/${res.data.total} platforms succeeded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Posting failed");
    } finally {
      setPosting(false);
    }
  };

  const togglePlatformEnabled = (pid) =>
    setSettings(prev => ({
      ...prev,
      enabled_platforms: prev.enabled_platforms.includes(pid)
        ? prev.enabled_platforms.filter(p => p !== pid)
        : [...prev.enabled_platforms, pid],
    }));

  const toggleManual = (pid) =>
    setManualPlatforms(prev => prev.includes(pid) ? prev.filter(p => p !== pid) : [...prev, pid]);

  const setCredField = (platform, field, val) =>
    setEditCreds(prev => ({ ...prev, [platform]: { ...prev[platform], [field]: val } }));

  // ── Styles ──────────────────────────────────────────────────────────────────
  const card  = `rounded-2xl p-5 mb-4 ${dm ? "bg-[#1a2122]" : "bg-white"}`;
  const lbl   = `category-tag text-xs mb-1 block ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`;
  const inp   = `w-full rounded-lg px-3 py-2 text-sm border ${dm ? "bg-[#0c1011] border-white/8 text-slate-200 placeholder:text-slate-600" : "bg-white border-[#e5e7eb] text-[#191c1d] placeholder:text-[#bac9cc]"} focus:outline-none focus:ring-2 focus:ring-[#006874]/30`;
  const h3txt = `font-medium ${dm ? "text-slate-100" : "text-[#191c1d]"}`;
  const subTx = `text-xs ${dm ? "text-slate-500" : "text-[#bac9cc]"}`;

  if (loading) {
    return (
      <TabsContent value="social">
        <div className={`flex items-center justify-center py-20 ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading social config…
        </div>
      </TabsContent>
    );
  }

  return (
    <TabsContent value="social">
      <div className="max-w-2xl space-y-4">

        {/* ── Auto-Post Settings ──────────────────────────────────────────── */}
        <div className={card} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1),0 4px 24px rgba(25,28,29,0.04)" }}>
          <div className="flex items-center gap-2 mb-4">
            <Settings className="w-4 h-4 text-[#006874]" />
            <h3 className={h3txt}>Auto-Post Settings</h3>
          </div>
          <div className="flex items-center gap-3 mb-4">
            <Switch
              checked={settings.auto_post_on_publish}
              onCheckedChange={v => setSettings(p => ({ ...p, auto_post_on_publish: v }))}
              className="data-[state=checked]:bg-[#006874]"
              data-testid="auto-post-toggle"
            />
            <div>
              <p className={`text-sm font-medium ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
                Auto-post when Brief is published
              </p>
              <p className={subTx}>Automatically share to selected platforms on publish</p>
            </div>
          </div>
          {settings.auto_post_on_publish && (
            <div className="mb-4">
              <p className={`${lbl} mb-2`}>Platforms to auto-post to</p>
              <div className="flex flex-wrap gap-2">
                {PLATFORMS.map(({ id, label, icon: Icon }) => {
                  const active = settings.enabled_platforms.includes(id);
                  return (
                    <button
                      key={id}
                      data-testid={`auto-platform-${id}`}
                      onClick={() => togglePlatformEnabled(id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        active
                          ? "border-[#006874] bg-[#006874]/10 text-[#006874]"
                          : dm ? "border-white/10 text-slate-400" : "border-[#e5e7eb] text-[#6b7a7d]"
                      }`}
                    >
                      <Icon className="w-3 h-3" /> {label}
                      {!isSet[id] && <span className="text-amber-500 ml-1">!</span>}
                    </button>
                  );
                })}
              </div>
              {settings.enabled_platforms.some(p => !isSet[p]) && (
                <p className="text-xs text-amber-500 mt-2">⚠ Some selected platforms have no credentials saved yet.</p>
              )}
            </div>
          )}
          <button
            onClick={saveCreds}
            disabled={saving}
            data-testid="save-social-settings-btn"
            className="px-4 py-2 rounded-lg text-sm font-medium bg-[#006874] text-white hover:bg-[#005a65] disabled:opacity-50 transition-colors"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : null}
            Save Settings
          </button>
        </div>

        {/* ── Manual Post ─────────────────────────────────────────────────── */}
        <div className={card} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1),0 4px 24px rgba(25,28,29,0.04)" }}>
          <div className="flex items-center gap-2 mb-4">
            <Send className="w-4 h-4 text-[#006874]" />
            <h3 className={h3txt}>Post Brief Now</h3>
          </div>
          <p className={`text-sm mb-3 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
            Manually post the current brief to selected platforms.
          </p>
          <div className="flex flex-wrap gap-2 mb-4">
            {PLATFORMS.map(({ id, label, icon: Icon }) => {
              const sel = manualPlatforms.includes(id);
              return (
                <button
                  key={id}
                  data-testid={`manual-platform-${id}`}
                  onClick={() => toggleManual(id)}
                  disabled={!isSet[id]}
                  title={!isSet[id] ? `${label} credentials not saved` : `Post to ${label}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                    !isSet[id]
                      ? "opacity-40 cursor-not-allowed border-dashed " + (dm ? "border-white/10 text-slate-500" : "border-[#e5e7eb] text-[#bac9cc]")
                      : sel
                      ? "border-[#006874] bg-[#006874]/10 text-[#006874]"
                      : dm ? "border-white/10 text-slate-300 hover:border-[#006874]/40" : "border-[#e5e7eb] text-[#3b494c] hover:border-[#006874]/40"
                  }`}
                >
                  <Icon className="w-3 h-3" /> {label}
                </button>
              );
            })}
          </div>
          <button
            onClick={postBriefNow}
            disabled={posting || !manualPlatforms.length}
            data-testid="post-brief-now-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-[#006874] text-white hover:bg-[#005a65] disabled:opacity-50 transition-colors"
          >
            {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
            {posting ? "Posting…" : "Post Now"}
          </button>

          {postResults && (
            <div className="mt-4 space-y-2">
              {postResults.map((r, i) => (
                <div key={i} className={`flex items-start gap-2 rounded-lg px-3 py-2 text-xs ${
                  r.success
                    ? dm ? "bg-emerald-900/20 text-emerald-400" : "bg-emerald-50 text-emerald-700"
                    : dm ? "bg-red-900/20 text-red-400" : "bg-red-50 text-red-700"
                }`}>
                  {r.success
                    ? <CheckCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    : <XCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />}
                  <span><strong className="capitalize">{r.platform}</strong> — {r.success ? (r.post_id || r.tweet_id || r.media_id || "Posted!") : r.message}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Platform Credentials ─────────────────────────────────────────── */}
        <div className={`${dm ? "text-slate-100" : "text-[#191c1d]"} font-medium text-sm mb-1 px-1`}>
          Platform Credentials
        </div>

        {PLATFORMS.map(({ id, label, icon: Icon, color, fields }) => {
          const isOpen = expanded[id];
          const set = isSet[id];
          const tr = testResults[id];
          return (
            <div key={id} className={card} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1),0 4px 24px rgba(25,28,29,0.04)" }}>
              <button
                className="w-full flex items-center justify-between"
                onClick={() => setExpanded(p => ({ ...p, [id]: !isOpen }))}
                data-testid={`expand-${id}-creds`}
              >
                <div className="flex items-center gap-2.5">
                  <Icon className="w-4 h-4" style={{ color }} />
                  <span className={`text-sm font-medium ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>{label}</span>
                  {set
                    ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600">Connected</span>
                    : <span className={`text-xs px-2 py-0.5 rounded-full ${dm ? "bg-white/5 text-slate-500" : "bg-[#f3f4f5] text-[#bac9cc]"}`}>Not set</span>
                  }
                </div>
                {isOpen ? <ChevronUp className="w-4 h-4 text-[#6b7a7d]" /> : <ChevronDown className="w-4 h-4 text-[#6b7a7d]" />}
              </button>

              {isOpen && (
                <div className="mt-4 space-y-3">
                  {fields.map(({ key, label: flabel, hint }) => (
                    <div key={key}>
                      <label className={lbl}>{flabel}{hint && <span className="ml-1 opacity-60">— {hint}</span>}</label>
                      <input
                        type="password"
                        className={inp}
                        placeholder={set ? "••••••••••• (saved)" : `Enter ${flabel}`}
                        value={editCreds[id]?.[key] || ""}
                        onChange={e => setCredField(id, key, e.target.value)}
                        data-testid={`cred-${id}-${key}`}
                        autoComplete="off"
                      />
                    </div>
                  ))}

                  <div className="flex items-center gap-2 pt-1">
                    <button
                      onClick={() => testPlatform(id)}
                      disabled={!set || testing[id]}
                      data-testid={`test-${id}-btn`}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                        dm ? "border-white/10 text-slate-300 hover:border-[#006874]/50 disabled:opacity-40"
                           : "border-[#e5e7eb] text-[#3b494c] hover:border-[#006874]/50 disabled:opacity-40"
                      }`}
                    >
                      {testing[id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                      Test Connection
                    </button>
                    {!set && <span className={`text-xs ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>Save credentials first to test</span>}
                  </div>

                  {tr && (
                    <div className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs ${
                      tr.success
                        ? dm ? "bg-emerald-900/20 text-emerald-400" : "bg-emerald-50 text-emerald-700"
                        : dm ? "bg-red-900/20 text-red-400" : "bg-red-50 text-red-700"
                    }`}>
                      {tr.success ? <CheckCircle className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                      {tr.message}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* ── Save All Credentials ────────────────────────────────────────── */}
        <button
          onClick={saveCreds}
          disabled={saving}
          data-testid="save-all-creds-btn"
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-medium bg-[#006874] text-white hover:bg-[#005a65] disabled:opacity-50 transition-colors"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
          {saving ? "Saving…" : "Save All Credentials"}
        </button>

      </div>
    </TabsContent>
  );
}
